import type { BBox, Dimension, Vec3 } from "@cfg/kernel";

/**
 * Pure geometry for dimension lines (cotas): turns a design's `Dimension`s into line
 * segments plus a label point, in model mm. `Dimensions.tsx` draws the result; keeping
 * the maths here makes it testable without WebGL.
 */

export interface DimensionDrawing {
  param: string;
  kind: Dimension["kind"];
  /** The dimension line itself, with its arrowheads (pairs of points). */
  lines: [Vec3, Vec3][];
  /** Extension lines, circle outlines and angle arms: drawn fainter. */
  guides: [Vec3, Vec3][];
  /** Where the value label sits. */
  label: Vec3;
}

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: Vec3) => Math.hypot(a[0], a[1], a[2]);
const norm = (a: Vec3): Vec3 => {
  const l = len(a);
  return l < 1e-9 ? [0, 0, 0] : mul(a, 1 / l);
};

/** Any unit vector perpendicular to `n` (prefers lying in the XY plane, so lines read flat). */
export function perpendicular(n: Vec3): Vec3 {
  const u = norm(n);
  const ref: Vec3 = Math.abs(u[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  return norm(cross(u, ref));
}

/** Arrow size for a part of this size: 3 % of its largest side, 0.8–4 mm. */
export function arrowSize(bbox: BBox | null): number {
  if (!bbox) return 2;
  const extent = Math.max(bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]);
  return Math.min(4, Math.max(0.8, extent * 0.03));
}

/** An arrowhead at `tip` pointing along unit `dir`, its wings in the plane of `side`. */
function arrow(tip: Vec3, dir: Vec3, side: Vec3, size: number): [Vec3, Vec3][] {
  const back = sub(tip, mul(dir, size));
  const w = mul(side, size * 0.35);
  return [
    [tip, add(back, w)],
    [tip, sub(back, w)],
  ];
}

function linear(d: Extract<Dimension, { kind: "linear" }>, size: number): DimensionDrawing {
  const offset = d.offset ?? [0, 0, 0];
  const a = add(d.from, offset);
  const b = add(d.to, offset);
  const u = norm(sub(b, a));
  const n = len(offset) > 1e-9 ? norm(offset) : perpendicular(u);
  const head = Math.min(size, len(sub(b, a)) / 3);
  const guides: [Vec3, Vec3][] = [];
  // Extension lines run from the part to a little past the dimension line.
  if (len(offset) > 1e-9) {
    const past = mul(n, size * 0.6);
    guides.push([d.from, add(a, past)], [d.to, add(b, past)]);
  }
  return {
    param: d.param,
    kind: d.kind,
    lines: [[a, b], ...arrow(a, mul(u, -1), n, head), ...arrow(b, u, n, head)],
    guides,
    label: mul(add(a, b), 0.5),
  };
}

function circle(center: Vec3, u: Vec3, v: Vec3, r: number, from = 0, to = Math.PI * 2, segments = 48): [Vec3, Vec3][] {
  const out: [Vec3, Vec3][] = [];
  const n = Math.max(2, Math.ceil((segments * Math.abs(to - from)) / (Math.PI * 2)));
  let prev = add(center, add(mul(u, r * Math.cos(from)), mul(v, r * Math.sin(from))));
  for (let i = 1; i <= n; i++) {
    const t = from + ((to - from) * i) / n;
    const p = add(center, add(mul(u, r * Math.cos(t)), mul(v, r * Math.sin(t))));
    out.push([prev, p]);
    prev = p;
  }
  return out;
}

function round(d: Extract<Dimension, { kind: "diameter" | "radius" }>, size: number): DimensionDrawing {
  const n = norm(d.normal);
  let u = d.direction ? norm(sub(d.direction, mul(n, dot(d.direction, n)))) : perpendicular(n);
  if (len(u) < 1e-9) u = perpendicular(n);
  const v = cross(n, u);
  const head = Math.min(size, d.radius / 2);
  const end = add(d.center, mul(u, d.radius));
  const guides = circle(d.center, u, v, d.radius);
  if (d.kind === "radius") {
    return { param: d.param, kind: d.kind, lines: [[d.center, end], ...arrow(end, u, v, head)], guides, label: add(d.center, mul(u, d.radius / 2)) };
  }
  const start = sub(d.center, mul(u, d.radius));
  return {
    param: d.param,
    kind: d.kind,
    lines: [[start, end], ...arrow(start, mul(u, -1), v, head), ...arrow(end, u, v, head)],
    guides,
    label: d.center,
  };
}

function angle(d: Extract<Dimension, { kind: "angle" }>, size: number): DimensionDrawing {
  const a = sub(d.from, d.center);
  const b = sub(d.to, d.center);
  const ua = norm(a);
  const ub = norm(b);
  const r = d.radius ?? Math.min(len(a), len(b)) * 0.8;
  const guides: [Vec3, Vec3][] = [
    [d.center, d.from],
    [d.center, d.to],
  ];
  const cos = Math.max(-1, Math.min(1, dot(ua, ub)));
  const theta = Math.acos(cos);
  // w: unit vector in the arms' plane, perpendicular to `ua`, on `ub`'s side.
  let w = norm(sub(ub, mul(ua, cos)));
  if (len(w) < 1e-9) w = perpendicular(ua);
  if (theta < 1e-6 || r <= 0) return { param: d.param, kind: d.kind, lines: [], guides, label: add(d.center, mul(ua, r)) };
  const lines = circle(d.center, ua, w, r, 0, theta, 48);
  const head = Math.min(size, (r * theta) / 3);
  const start = add(d.center, mul(ua, r));
  const end = add(d.center, add(mul(ua, r * Math.cos(theta)), mul(w, r * Math.sin(theta))));
  const tanEnd = norm(add(mul(ua, -Math.sin(theta)), mul(w, Math.cos(theta))));
  const radialEnd = norm(sub(end, d.center));
  lines.push(...arrow(start, mul(w, -1), ua, head), ...arrow(end, tanEnd, radialEnd, head));
  // Label past the ends of the arms, on the bisector, so it covers neither the arc nor whatever sits at the centre.
  const mid = theta / 2;
  const out = Math.max(r, len(a), len(b)) * 1.15;
  const label = add(d.center, add(mul(ua, out * Math.cos(mid)), mul(w, out * Math.sin(mid))));
  return { param: d.param, kind: d.kind, lines, guides, label };
}

/** Segments and label point for one dimension; arrowheads scale with `size` (mm). */
export function drawDimension(d: Dimension, size: number): DimensionDrawing {
  if (d.kind === "linear") return linear(d, size);
  if (d.kind === "angle") return angle(d, size);
  return round(d, size);
}

/**
 * Which dimensions to show, highest priority first: every one of the active param, then
 * (unless `all`) those of the first `primary` dimensioned params in form order. Several
 * dimensions may share a param; they show and hide together.
 */
export function pickDimensions<D extends { param: string }>(dims: D[], active: string | null, primary: number, order?: string[], all = false): D[] {
  const params: string[] = [];
  for (const d of dims) if (!params.includes(d.param)) params.push(d.param);
  if (order?.length) {
    const rank = new Map(order.map((n, i) => [n, i]));
    params.sort((a, b) => (rank.get(a) ?? 1e9) - (rank.get(b) ?? 1e9));
  }
  let shown = all ? params : params.slice(0, primary);
  if (active && params.includes(active)) shown = [active, ...shown.filter((p) => p !== active)];
  return shown.flatMap((p) => dims.filter((d) => d.param === p));
}

/** "Ø 24 mm", "R 12 mm", "45°", "4 studs". */
export function dimensionText(kind: Dimension["kind"], value: unknown, unit?: string): string {
  const v = typeof value === "number" ? String(Number(value.toFixed(2))) : String(value ?? "");
  const withUnit = unit ? (unit === "°" ? `${v}°` : `${v} ${unit}`) : v;
  return kind === "diameter" ? `Ø ${withUnit}` : kind === "radius" ? `R ${withUnit}` : withUnit;
}
